import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAccountContext } from '@/app/accounts';
import { lazyComponent } from '@/app/lazyWithPreload';
import { MAIN_CONTENT_ID } from '@/components/SkipLink';

/*
 * Its own chunk (#208's rule: nothing off the first paint's path in the
 * entry), declared here rather than in app/lazyUi.ts: the shell's switcher and
 * sidebar import this module, and lazyUi's other dialogs have no business on
 * their import graph.
 */
const AccountReauthDialog = lazyComponent(() => import('./AccountReauthDialog'), 'AccountReauthDialog');

/**
 * Asks for a connected mailbox's own password, over the app (#192, plan 06 §6
 * step 3): "blocking, only on intent".
 *
 * The shell banner and the amber dot say a mailbox is flagged; this is what
 * opens when the user then acts on it anyway (its Re-enter password, opening
 * its folders, switching to it, sending as it). It used to be a link to
 * Settings → Accounts, which took the user away from what they were doing to
 * a page of cards; the dialog keeps them where they were.
 *
 * Mounted once, by the shell. Outside it (a popped-out window, a component
 * test) {@link useReauthPrompt} is `undefined` and callers keep their link to
 * Settings, which still has the same form.
 */
export type ReauthPrompt = (accountId: string, opener?: HTMLElement | null) => void;

const ReauthPromptContext = createContext<ReauthPrompt | undefined>(undefined);

export function useReauthPrompt(): ReauthPrompt | undefined {
  return useContext(ReauthPromptContext);
}

interface Open {
  accountId: string;
  opener: HTMLElement | null;
}

export function ReauthPromptProvider({ children }: { children: ReactNode }) {
  const { accounts } = useAccountContext();
  const [open, setOpen] = useState<Open | null>(null);
  /** Where focus goes once the dialog is gone; `undefined` when nothing is pending. */
  const restore = useRef<HTMLElement | null | undefined>(undefined);

  const prompt = useCallback<ReauthPrompt>((accountId, opener) => {
    const active = document.activeElement;
    setOpen({
      accountId,
      // `null` says "nothing to go back to" (the opener closes with its menu).
      opener:
        opener !== undefined
          ? opener
          : active instanceof HTMLElement && active !== document.body
            ? active
            : null,
    });
  }, []);

  const close = useCallback(() => {
    setOpen((current) => {
      restore.current = current?.opener ?? null;
      return null;
    });
  }, []);

  /*
   * Focus back to whatever opened the dialog (plan 06 §11 "Dialogs"). Often
   * that control is gone by then: the banner row, the tree's "needs its
   * password" branch and the switcher's menu all go away once the mailbox is
   * fixed. Checked a task later, after the account list's own update has
   * landed, and the page's main region takes it instead of <body>.
   */
  useEffect(() => {
    if (open !== null || restore.current === undefined) return;
    const target = restore.current;
    restore.current = undefined;
    const timer = window.setTimeout(() => {
      if (target?.isConnected) target.focus({ preventScroll: true });
      else document.getElementById(MAIN_CONTENT_ID)?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Its chunk is fetched once a mailbox is flagged, so the first Re-enter
  // password does not wait on the network.
  const anyFlagged = accounts.some((candidate) => !candidate.isPrimary && candidate.status === 'reauth-required');
  useEffect(() => {
    if (anyFlagged) void AccountReauthDialog.preload().catch(() => undefined);
  }, [anyFlagged]);

  const account = open ? accounts.find((candidate) => candidate.id === open.accountId) : undefined;
  // The primary mailbox failing is session expiry (plan 06 §2), never this.
  const showing = account !== undefined && !account.isPrimary ? account : undefined;

  return (
    <ReauthPromptContext.Provider value={prompt}>
      {children}
      {showing && <AccountReauthDialog key={showing.id} account={showing} onClose={close} />}
    </ReauthPromptContext.Provider>
  );
}
